import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Notification, ModalState } from '@/types';

interface UIStore {
  // Notifications
  notifications: Notification[];
  
  // Modals
  modals: Record<string, ModalState>;
  
  // Loading states
  globalLoading: boolean;
  loadingMessage: string;
  
  // Layout
  sidebarOpen: boolean;
  
  // Actions
  showNotification: (notification: Omit<Notification, 'id'>) => void;
  hideNotification: (id: string) => void;
  clearNotifications: () => void;
  
  openModal: (id: string, modal: Omit<ModalState, 'isOpen'>) => void;
  closeModal: (id: string) => void;
  closeAllModals: () => void;
  
  setGlobalLoading: (loading: boolean, message?: string) => void;
  
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
}

export const useUIStore = create<UIStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      notifications: [],
      modals: {},
      globalLoading: false,
      loadingMessage: '',
      sidebarOpen: false,

      // Notification actions
      showNotification: (notification) => {
        const id = crypto.randomUUID();
        const newNotification: Notification = {
          id,
          ...notification,
          duration: notification.duration ?? 5000,
        };

        set(state => ({
          notifications: [...state.notifications, newNotification]
        }));

        // Auto-hide after duration
        if (newNotification.duration && newNotification.duration > 0) {
          setTimeout(() => {
            get().hideNotification(id);
          }, newNotification.duration);
        }
      },

      hideNotification: (id: string) => {
        set(state => ({
          notifications: state.notifications.filter(n => n.id !== id)
        }));
      },

      clearNotifications: () => {
        set({ notifications: [] });
      },

      // Modal actions
      openModal: (id: string, modal) => {
        set(state => ({
          modals: {
            ...state.modals,
            [id]: { ...modal, isOpen: true }
          }
        }));
      },

      closeModal: (id: string) => {
        set(state => ({
          modals: {
            ...state.modals,
            [id]: { ...state.modals[id], isOpen: false }
          }
        }));
      },

      closeAllModals: () => {
        const { modals } = get();
        const closedModals: Record<string, ModalState> = {};
        
        Object.keys(modals).forEach(id => {
          closedModals[id] = { ...modals[id], isOpen: false };
        });

        set({ modals: closedModals });
      },

      // Loading actions
      setGlobalLoading: (loading: boolean, message = '') => {
        set({
          globalLoading: loading,
          loadingMessage: message,
        });
      },

      // Layout actions
      toggleSidebar: () => {
        set(state => ({ sidebarOpen: !state.sidebarOpen }));
      },

      setSidebarOpen: (open: boolean) => {
        set({ sidebarOpen: open });
      },
    }),
    {
      name: 'ui-store',
    }
  )
);